<template>
  <div class="base-field">
    <label v-if="label" class="base-field__label">
      <span v-if="isRequired" class="base-field_required">*</span>
      {{ label }}
    </label>
    <vue-tel-input v-model="phoneNumber" :defaultCountry="country" :mode="mode" :autoFormat="false" :inputOptions="{
      placeholder: placeholder,
      type: type,
      showDialCode: false,
      validCharactersOnly: true,
    }" @input="validateInput" @country-changed="getCodeCountry"></vue-tel-input>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";

import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";

const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  mode: {
    type: String,
    default: "international",
  },
  country: {
    type: String,
    default: "US",
  },
  countryCode: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "tel",
  },
  label: {
    type: String,
    default: "",
  },
  showSearchBox: {
    type: Boolean,
    default: false,
  },
  showDialCode: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "tel",
  },
  name: {
    type: String,
    default: "",
  },
  error: {
    type: Array,
    required: false,
  },
  isPassword: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isTextCenter: {
    type: Boolean,
    default: false,
  },
  isGray: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["country-changed"]);
const phoneNumber = ref("");
const dialCode = ref("");

const tempNumber = ref("");
const isDropdownOpen = ref(false);

const validateInput = (val) => {
  if (!isDropdownOpen.value) {
    phoneNumber.value = phoneNumber.value.replace(/[^0-9+()-\s]/g, "");
  } else {
    phoneNumber.value = tempNumber.value;
  }
};

const getCodeCountry = (countryObj) => {
  dialCode.value = `${countryObj.dialCode}`;
  const vueTelInput = document.querySelector(".vue-tel-input");
  const dropdown = vueTelInput?.querySelector(".vti__dropdown");
  vueTelInput?.querySelector(".vti__code").remove();
  dropdown.insertAdjacentHTML(
    "afterend",
    `<div class="vti__code">+${dialCode.value}</div>`
  );
  emit("country-changed", dialCode.value);
};

watch(phoneNumber, (newVal, oldVal) => {
  const dropdown = document.querySelector(".vti__dropdown");
  if (dropdown?.classList.contains("open")) {
    if (!isDropdownOpen.value) {
      isDropdownOpen.value = true;
      tempNumber.value = oldVal;
    }
  } else {
    isDropdownOpen.value = false;
  }
});

onMounted(() => {
  insertCountryCode();
});

const insertCountryCode = () => {
  const vueTelInput = document.querySelector(".vue-tel-input");
  const dropdown = vueTelInput?.querySelector(".vti__dropdown");
  dropdown.insertAdjacentHTML(
    "afterend",
    `<div class="vti__code">${dialCode.value}</div>`
  );
};
</script>
<style lang="scss">
.base-field {
  position: relative;
  font-size: 20px;
  // margin-bottom: 10px;

  &__label {
    color: #0D141C;
    min-height: 30px;
    margin-bottom: 0;
    font-size: 14px;
  }

  &_required {
    color: #ff0000;
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 0 15px;
    line-height: 145%;
    border: 1px solid rgba(23, 23, 25, 0.1);
    border-radius: 12px;
    transition: 0.2s ease-in-out;

    &:focus-within {
      border-color: #a1a1aa;
    }
  }

  &__input {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    color: #9696a2;
    font-size: 14px;

    &::placeholder {
      color: #52525b;
      font-size: 16px;
    }
  }

  &__slot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0;
    height: 100%;

    span {
      width: 25px;

      &:before {
        font-size: 26px;
      }
    }
  }

  &__error {
    margin-top: 2px;
    border-radius: 10px;
    font-size: 12px;
    color: #f87171;
    padding: 2px 10px;
  }

  .ui-kit {
    &__icon {
      &::before {
        font-size: 20px;
      }
    }
  }

  &_is-password {
    &:focus-within {
      .ui-kit {
        &__icon {
          &::before {
            color: #a3e635;
          }
        }
      }
    }
  }

  &_is-readonly {
    .base-field__control {
      background: rgba(255, 255, 255, 0.05);
      cursor: not-allowed;
    }

    .base-field__input {
      &:read-only {
        color: #3f3f46;
        cursor: not-allowed;
      }
    }
  }

  &_is-border {
    .base-field__control {
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:focus-within {
        border-color: #a1a1aa;
      }
    }
  }

  &_is-bottom-border-focus {
    gap: 8px;
    display: flex;
    flex-direction: column;

    .base-field__label {
      font-size: 12px;
      line-height: 16px;
      min-height: auto;

    }

    .base-field__control {
      background: var(--background-secondary);
      border: none;
      border-bottom: 1px solid transparent;
      transition: border-bottom 0.3s ease;
    }

    .base-field__control:focus-within {
      border: none;
      border-bottom: 1px solid var(--foreground-primary);
    }

    .base-field__input {
      font-size: 14px;
      line-height: 20px;
      color: var(--foreground-primary);
    }

    .base-field__input::placeholder {
      font-size: 14px;
      line-height: 20px;
      color: #52525b;
    }

    .base-field__error {
      color: #f87171;
      padding: 0;
    }

    .base-field__control:has(+ .base-field__error) {
      border: none;
      border-bottom: 1px solid #dc2626;
    }
  }

  &_is-search {
    .base-field__control {
      background: var(--search-bg);
    }
  }

  &_is-gray {
    .base-field__control {
      background: var(--background-secondary);
    }
  }

  &_is-center &__input {
    text-align: center;
  }

  &_is-center {
    min-width: 100px;
  }

  @include _380 {
    font-size: 14px;

    &__control {
      font-size: 14px;
      height: 44px;
    }
  }
}

.sign-in {
  &__field {
    &-dropdown {
      .vue-tel-input {
        position: relative;
        z-index: 100;
        height: 48px;
        border-radius: 10px;
        // border: 1px solid #a1a1aa;
        border: 1px solid #CFDBE8;
        background-color: var(--theme-wise-background);


        .vti__input {
          background: var(--theme-wise-background);
          border-radius: 0 10px 10px 0;
          color: var(--foreground-primary);
          font-size: 16px;
          padding-left: 10px;
          padding-right: 10px;
          font-family: "SF Pro Text", sans-serif;
          color: var(--text-primary);

          &::placeholder {
            color: var(--foreground-secondary);
            font-size: 14px;
            transition: opacity 0.3s ease-in-out;
          }

          &:focus::placeholder {
            opacity: 0;
          }

          &.vti__search_box {
            width: calc(100% - 10px);
            border-radius: 5px;
          }
        }

        .vti__phone {
          padding-left: 0;
        }

        .vti__dropdown {
          position: static;
          border-right: 1px solid #a1a1aa;

          &:hover,
          &.open {
            background-color: transparent;
          }

          &:focus-visible {
            outline: none;
          }

          &-arrow {
            color: #a1a1aa;
          }

          &-list {
            width: 100%;
            border-radius: 10px;
            // background-color: var(--background-primary);
            border: 1px solid #a1a1aa;
            max-height: 136px;
            left: 0;

            &.below {
              top: calc(100% + 10px);
              scrollbar-width: none;
            }
          }

          &-item {
            padding: 10px;
            font-size: 13px;
            background-color: var(--theme-wise-background);

            strong {
              font-weight: normal;
            }

            &.highlighted {
              background-color: var(--theme-wise-hover);
            }
          }
        }

        .vti__code {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          padding: 0 8px 0 10px;
          opacity: 0.5;
          font-family: "SF Pro Text", sans-serif;
          // color: #4D7399;
        }

        &:focus-within {
          box-shadow: none;
        }
      }

      &.form-field--dropdown {
        .vue-tel-input {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(60px);
          border: none;

          .vti {
            &__input {
              background: transparent;
            }

            &__phone {
              &::placeholder {
                color: rgba(255, 255, 255, 0.5);
              }
            }

            &__dropdown {
              border-right: 1px solid rgba(142, 108, 134, 0.6);

              &-arrow {
                color: #fff;
              }

              &-list {
                background: #8d386c;
                backdrop-filter: blur(60px);
                scrollbar-width: thin;
                scrollbar-color: #930036 #d070b4;
              }
            }
          }
        }
      }
    }
  }
}
</style>
